/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, ReactNode, useEffect, useMemo } from 'react';
import { PageonModal, ErrorHandlerAlert } from '../../molecules';
import { useHQVContext } from '../../context/HQVContext';
import { HQV_ACTIONS } from '../../reducers';
import { constants } from '../../constants';
import { eventUtil, canUseDOM } from '@marriott/mi-ui-library';
import { useCheckBreakpoint } from '../../hooks/lib/useCheckBreakpoint';
import { getItemFromPageModel, getSessionIDFromCookie, respGridUtil, getMockPageModel } from '../../utils/src/helper';
import { PROPERTY_INFO_DATA } from '../../modules/constants';
import { useSearchResultsStore } from '../../modules/store/searchResultsStore';
import { useHQVDatalayerTrackingStore } from '../../modules/store/hqvDatalayerTrackingStore';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { MAP_VIEW_SESSION_KEY } from '../../modules/store/store.constants';

const { ROUTING_CONFIG, EXPERIENCE_SEGMENT, HQV_SEOURL, ITEM_PATH_ROOT, PAGE_NAME, SPA_APP_NAME } = constants;

export interface HQVWrapperProps {
  children: ReactNode;
  currentLocale: string;
  globalData?: any;
  model?: any;
}

export const HQVWrapper: FC<HQVWrapperProps> = ({ children, currentLocale, globalData, model }) => {
  // Get the full HQV context
  const { hqvDispatch, hqvState } = useHQVContext();

  // HQV Datalayer tracking store reset function
  const resetDatalayerTracking = useHQVDatalayerTrackingStore(state => state.resetStore);

  const isViewportM = useCheckBreakpoint('viewportM');
  const setSearchResultsState = useSearchResultsStore(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: { setSearchResultsState: any }) => state.setSearchResultsState
  );
  const { envVarsObject } = useClientEnvVarsStore();
  // Get session token
  let sessionToken = '';
  if (globalData?.sessionData && (globalData?.sessionData?.data || globalData?.sessionData?.cacheData)) {
    const sessionObj = globalData?.sessionData?.data ? globalData?.sessionData : globalData?.sessionData?.cacheData;
    sessionToken = sessionObj?.data?.sessionToken || sessionObj?.key || getSessionIDFromCookie() || '';
  }

  // Request body for AEM template call in which SEOURL will decide which template to load
  const getCMSTemplateReqBody = useMemo(
    () => ({
      requestType: ROUTING_CONFIG,
      seoUrl: HQV_SEOURL,
      localeKey: currentLocale,
      experienceSegment: EXPERIENCE_SEGMENT,
      applicationName: SPA_APP_NAME,
      sessionToken: sessionToken,
    }),
    [currentLocale, sessionToken]
  );

  // function to close the modal and reset the loader code
  const closeModal = () => {
    resetDatalayerTracking();
    //delete data for property info from store on close of HQV model
    setSearchResultsState([PROPERTY_INFO_DATA], {
      [PROPERTY_INFO_DATA]: { [PROPERTY_INFO_DATA]: {} },
    });
    const modalHeaderComp = document.getElementsByClassName('hqv-modal-content-header')[0] as HTMLElement;
    if (modalHeaderComp) modalHeaderComp.style.visibility = 'hidden';
    const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
    if (loaderComp) {
      loaderComp.style.visibility = 'visible';
      loaderComp.style.display = 'block';
      if (!isViewportM) {
        loaderComp.style.display = 'flex';
      }
    }
    const modalDataComp = document.getElementsByClassName('hqv-modal-data')[0] as HTMLElement;
    if (modalDataComp) {
      modalDataComp.style.visibility = 'hidden';
      modalDataComp.style.display = 'none';
    }
    hqvDispatch({ type: HQV_ACTIONS.MODAL_OPEN_STATE, payload: false });
    document.body.style.height = '100%';
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MAP_VIEW_SESSION_KEY) !== 'true') {
      document.body.style.overflow = 'auto';
    }
  };

  // function to hide the loader and show the actual content
  // using styling through JS so as to eliminate re-renders
  function showModalContent() {
    const modalHeaderComp = document.getElementsByClassName('hqv-modal-content-header')[0] as HTMLElement;
    if (modalHeaderComp) modalHeaderComp.style.visibility = 'visible';
    const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
    if (loaderComp) {
      loaderComp.style.visibility = 'hidden';
      loaderComp.style.display = 'none';
    }
    const modalDataComp = document.getElementsByClassName('hqv-modal-data')[0] as HTMLElement;
    if (modalDataComp) {
      modalDataComp.style.visibility = 'visible';
      modalDataComp.style.display = 'block';
    }
    const modalCloseButton = document.getElementsByClassName('popup-close')[0] as HTMLElement;
    modalCloseButton?.focus();
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    eventUtil.remove('HQVComponentCallCompleted', () => {});
  }

  // PageonModal useEffect hooks
  useEffect(() => {
    if (model?.enableHQVModelCall && model?.hideDestination) {
      const { AriesSearch, AriesReservation } = globalData?.sessionData?.cacheData?.data || {};

      // Extract propertyId from session data
      let propertyId = AriesSearch?.searchCriteria?.propertyId || AriesReservation?.propertyId;

      // If propertyId is invalid, try to get propertyCode from URL as fallback
      if (!propertyId || propertyId === 'undefined' || propertyId === 'null') {
        try {
          if (canUseDOM) {
            const urlParams = new URLSearchParams(window.location.search);
            const propertyCode = urlParams.get('propertyCode');
            if (propertyCode) {
              propertyId = propertyCode;
            }
          }
        } catch (error) {
          // Silently handle URL parsing errors
        }
      }

      // Only dispatch if we have a valid propertyId AND it's different from current state
      // This prevents clearing a valid propertyId during re-renders
      if (propertyId && propertyId !== 'undefined' && propertyId !== 'null') {
        // Only update if the propertyId is different from current state to prevent unnecessary updates
        if (hqvState.propertyIDvalue !== propertyId) {
          hqvDispatch({
            type: HQV_ACTIONS.PROPERTY_IDENTITY,
            payload: propertyId,
          });
        }
      }
    }
  }, [model?.enableHQVModelCall, model?.hideDestination, hqvState.propertyIDvalue]);

  useEffect(() => {
    eventUtil.on('showHotelDetailModal', () => {
      resetDatalayerTracking();
      hqvDispatch({ type: HQV_ACTIONS.MODAL_OPEN_STATE, payload: true });
      document.body.style.overflow = 'hidden';
    });
  }, [resetDatalayerTracking]);

  /**
   * Determines if HQV modal should be rendered. Requires:
   * - enableHQVModelCall flag to be true in model config
   * - NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT for template URL (PageonModal)
   * - NEXT_PUBLIC_AEM_PATH for AEM content path
   * Guards against rendering HQV when env vars are missing to avoid runtime errors.
   */
  const getHQVModel =
    model?.enableHQVModelCall === true &&
    envVarsObject?.['NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT'] &&
    envVarsObject?.['NEXT_PUBLIC_AEM_PATH'];

  // useEffect to add an event listener for the custom event
  useEffect(() => {
    if (getHQVModel) {
      eventUtil.on(`HQVComponentCallCompleted`, () => {
        // Checking if the loader is already hidden. If yes, no need to trigger the same code again
        const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
        if (loaderComp && loaderComp.style.visibility !== 'hidden') {
          showModalContent();
        }
      });
    }
    return (): void => {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      eventUtil.remove('HQVComponentCallCompleted', () => {});
    };
  }, [getHQVModel]);

  return (
    <>
      {children}
      {getHQVModel && (
        <PageonModal
          templateURL={envVarsObject['NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT']}
          modelReqBody={getCMSTemplateReqBody}
          hotelQuickViewCacheConfig={globalData?.sessionData?.hotelQuickViewCacheConfig}
          modalOpenState={hqvState.modalOpenState}
          closeModal={closeModal}
          operationSignatures={globalData?.sessionData?.operationSignatures}
          enableApolloUtility={false}
          aemPath={envVarsObject['NEXT_PUBLIC_AEM_PATH']}
          itemPath={ITEM_PATH_ROOT}
          pageName={PAGE_NAME}
          getMockPageModel={getMockPageModel}
          getItemFromPageModel={getItemFromPageModel}
          respGridUtil={respGridUtil}
          ErrorHandlerAlert={ErrorHandlerAlert}
          flexLayout={true}
        />
      )}
    </>
  );
};
